import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  Truck,
  MapPin,
  Pencil,
  Trash2,
} from 'lucide-react';
import { ShippableOrder } from '../components/ShippingFeeModal';
import PackageShippingModal from '../components/PackageShippingModal';

interface OrderItem {
  id?: string;
  retailer: string;
  url: string;
  price: number;
  category: string;
  quantity: number;
  size: string | null;
  color: string | null;
  discount_code: string | null;
  image_filename: string | null;
}

interface Order extends ShippableOrder {
  id: string;
  user_id: string;
  status: string;
  total_amount: number;
  currency: string;
  created_at: string;
  items: OrderItem[];
  tracking_number: string | null;
  carrier: string | null;
  tracking_url: string | null;
  delivery_status: string | null;
  delivery_estimated: string | null;
  delivered_at: string | null;
  invoice_status?: string;
  invoice_rejected_reason?: string | null;
  reward_credited?: boolean;
  reward_credited_amount?: number | null;
  actual_invoice_amount?: number | null;
  package_id?: string | null;
  payment_method?: string | null;
  shipping_country?: string | null;
  shipping_address?: string | null;
}

const paymentMethodLabels: Record<string, string> = {
  card: 'Card (Stripe)',
  bank_transfer: 'Bank Transfer',
  reward: 'Reward Balance',
};

const parseShippingAddress = (raw?: string | null): string | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed;
    return Object.values(parsed).filter(Boolean).join(', ');
  } catch {
    return raw;
  }
};

interface PackageInfo {
  id: string;
  order_ids: string[];
  shipping_fee_amount: number | null;
  shipping_fee_payment_method: string | null;
  shipping_fee_paid: boolean;
}

const statusConfig: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  pending_preauth: {
    label: 'Pending Pre-Auth',
    icon: <Clock size={14} />,
    color: '#F59E0B',
    bg: '#FEF9C3',
  },
  preauthorized: {
    label: 'Order Locked',
    icon: <CheckCircle size={14} />,
    color: '#8B5CF6',
    bg: '#F3E8FF',
  },
  confirmed: {
    label: 'Confirmed — Purchasing',
    icon: <CheckCircle size={14} />,
    color: '#0891B2',
    bg: '#ECFEFF',
  },
  captured: {
    label: 'Paid & Procured',
    icon: <CheckCircle size={14} />,
    color: '#2563EB',
    bg: '#EFF6FF',
  },
  shipped: {
    label: 'Shipped',
    icon: <Truck size={14} />,
    color: '#EA580C',
    bg: '#FFF7ED',
  },
  delivered: {
    label: 'Delivered',
    icon: <MapPin size={14} />,
    color: '#16A34A',
    bg: '#DCFCE7',
  },
  released: {
    label: 'Cancelled',
    icon: <XCircle size={14} />,
    color: '#EF4444',
    bg: '#FEF2F2',
  },
  fulfilled: {
    label: 'Shipped',
    icon: <Truck size={14} />,
    color: '#10B981',
    bg: '#DCFCE7',
  },
};

const deliveryStatusConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  pending: { label: 'Awaiting Dispatch', icon: <Clock size={14} />, color: '#F59E0B' },
  in_transit: { label: 'In Transit', icon: <Truck size={14} />, color: '#2563EB' },
  delivered: { label: 'Delivered ✓', icon: <MapPin size={14} />, color: '#10B981' },
  exception: { label: 'Delivery Exception', icon: <XCircle size={14} />, color: '#EF4444' },
};

const Orders: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editItems, setEditItems] = useState<OrderItem[]>([]);
  const [editTotalAmount, setEditTotalAmount] = useState('0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmingDeliveryId, setConfirmingDeliveryId] = useState<string | null>(null);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [packages, setPackages] = useState<Record<string, PackageInfo>>({});
  const [creatingPackage, setCreatingPackage] = useState(false);
  const [packageModal, setPackageModal] = useState<{ id: string; orderCount: number } | null>(null);
  const [itemImageUrls, setItemImageUrls] = useState<Record<string, string>>({});
  const [uploadingImageItemId, setUploadingImageItemId] = useState<string | null>(null);

  // BDD 1-A line 7 (「圖片」, todo.txt 20.1) — item images require an
  // Authorization header, so they're fetched as blobs and shown via object
  // URLs, same pattern as the KYC photo previews on AdminUserProfileDetail.tsx.
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    orders.forEach((order) => {
      order.items.forEach((item) => {
        if (item.image_filename && item.id && !itemImageUrls[item.id]) {
          axios
            .get(API.getOrderItemImage(order.id, item.id), { headers, responseType: 'blob' })
            .then((res) => setItemImageUrls((current) => ({ ...current, [item.id!]: window.URL.createObjectURL(res.data) })))
            .catch(() => {});
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const uploadItemImage = async (orderId: string, itemId: string, file: File) => {
    setUploadingImageItemId(itemId);
    const token = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(API.uploadOrderItemImage(orderId, itemId), formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setItemImageUrls((current) => {
        const next = { ...current };
        delete next[itemId];
        return next;
      });
      await fetchOrders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload item image.');
    } finally {
      setUploadingImageItemId(null);
    }
  };

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const response = await axios.get(API.listOrders(), { headers });
      const loadedOrders: Order[] = response.data;
      setOrders(loadedOrders);

      // BDD 1-A line 41: fetch shared shipping-fee status for any order
      // that's part of a combined package.
      const packageIds = Array.from(new Set(loadedOrders.map((o) => o.package_id).filter(Boolean))) as string[];
      const fetchedPackages = await Promise.all(
        packageIds.map((id) => axios.get(API.getPackage(id), { headers }).then((res) => res.data).catch(() => null)),
      );
      setPackages(Object.fromEntries(fetchedPackages.filter(Boolean).map((pkg: PackageInfo) => [pkg.id, pkg])));
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError('Failed to load smart orders.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds((current) => (
      current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId]
    ));
  };

  const combineSelectedOrders = async () => {
    if (selectedOrderIds.length === 0) return;
    setCreatingPackage(true);
    setError('');
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const response = await axios.post(API.createPackage(), { order_ids: selectedOrderIds }, { headers });
      setPackageModal({ id: response.data.id, orderCount: selectedOrderIds.length });
      setSelectedOrderIds([]);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to combine the selected orders.');
    } finally {
      setCreatingPackage(false);
    }
  };

  const startEditing = (order: Order) => {
    setEditingOrderId(order.id);
    setEditItems(order.items.map((item) => ({ ...item })));
    setEditTotalAmount(order.total_amount.toString());
    setError('');
  };

  const updateItem = (index: number, field: keyof OrderItem, value: string) => {
    setEditItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      if (field === 'price') return { ...item, price: Number(value) };
      if (field === 'quantity') return { ...item, quantity: Math.max(1, parseInt(value, 10) || 1) };
      return { ...item, [field]: value };
    }));
  };

  const addItem = () => {
    setEditItems((current) => [...current, { retailer: '', url: '', price: 0, category: '', quantity: 1, size: '', color: '', discount_code: '', image_filename: null }]);
  };

  const removeItem = (index: number) => {
    setEditItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const saveOrder = async () => {
    if (!editingOrderId) return;
    setSaving(true);
    setError('');
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.put(
        API.updateOrder(editingOrderId),
        {
          total_amount: Number(editTotalAmount),
          currency: 'gbp',
          items: editItems.map((item) => ({
            retailer: item.retailer,
            url: item.url,
            price: Number(item.price),
            category: item.category,
            quantity: item.quantity || 1,
            size: item.size || undefined,
            color: item.color || undefined,
            discount_code: item.discount_code || undefined,
          })),
        },
        { headers },
      );
      setEditingOrderId(null);
      await fetchOrders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update order.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelivery = async (order: Order) => {
    if (!window.confirm('Confirm you have received this package? This lets Incord verify the invoice and release your Bonus Reward.')) {
      return;
    }
    setConfirmingDeliveryId(order.id);
    setError('');
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(API.confirmDelivery(order.id), {}, { headers });
      await fetchOrders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to confirm delivery.');
    } finally {
      setConfirmingDeliveryId(null);
    }
  };

  const deleteOrder = async (order: Order) => {
    if (!window.confirm('Delete this purchase request? This cannot be undone.')) {
      return;
    }
    setDeletingOrderId(order.id);
    setError('');
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(API.deleteOrder(order.id), { headers });
      await fetchOrders();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete order.');
    } finally {
      setDeletingOrderId(null);
    }
  };

  if (loading) return <div className="main-content">Loading orders...</div>;

  // BDD 1-A line 26 (「選擇單個或多個商品合併打包」): package-and-ship works the
  // same way whether one order is selected or several — it always creates a
  // Package (of size 1 or more) and routes through PackageShippingModal.
  const eligibleForCombining = orders.filter(
    (order) => order.status === 'captured' && !order.shipping_fee_paid && !order.shipping_fee_payment_method && !order.package_id,
  );

  return (
    <div className="main-content">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
        }}
      >
        <h1 className="page-title" style={{ margin: 0 }}>
          Smart Orders
        </h1>
        <Link to="/order" className="btn btn-primary">
          New Order
        </Link>
      </div>

      {eligibleForCombining.length > 0 && (
        <div
          style={{
            marginBottom: '20px',
            padding: '14px 20px',
            borderRadius: '8px',
            background: 'rgba(74, 93, 35, 0.06)',
            border: '1px solid rgba(74, 93, 35, 0.15)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <span style={{ fontSize: '13px' }}>
            {selectedOrderIds.length > 0
              ? `${selectedOrderIds.length} order${selectedOrderIds.length > 1 ? 's' : ''} selected — ${selectedOrderIds.length > 1 ? 'combine them into one package and pay a single shipping fee.' : 'package it and pay its shipping fee.'}`
              : `${eligibleForCombining.length} order${eligibleForCombining.length > 1 ? 's are' : ' is'} ready to ship. Select one or more to package and ship.`}
          </span>
          <button
            className="btn btn-primary"
            style={{ padding: '8px 16px', fontSize: '13px' }}
            disabled={selectedOrderIds.length === 0 || creatingPackage}
            onClick={combineSelectedOrders}
          >
            {creatingPackage
              ? 'Processing...'
              : selectedOrderIds.length > 1
                ? `Combine ${selectedOrderIds.length} Selected & Ship`
                : `Ship ${selectedOrderIds.length || ''} Selected Order${selectedOrderIds.length > 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: '#FEF2F2', color: '#991B1B' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gap: '24px' }}>
        {orders.length > 0
          ? orders.map((order) => {
              const status = statusConfig[order.status] || {
                label: order.status,
                icon: <Clock size={14} />,
                color: '#64748B',
                bg: '#F1F5F9',
              };
              const delivery = order.delivery_status
                ? deliveryStatusConfig[order.delivery_status]
                : null;

              return (
                <div key={order.id} className="section-card" style={{ padding: '24px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '20px',
                      flexWrap: 'wrap',
                      gap: '12px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Package size={20} color="var(--accent-moss)" />
                        <span style={{ fontWeight: 700, fontSize: '18px' }}>
                          Order #{order.id.split('-')[0].toUpperCase()}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748B', marginTop: '4px' }}>
                        Placed on {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {order.status === 'pending_preauth' && (
                        <>
                          <button className="btn btn-outline" style={{ padding: '8px 12px' }} onClick={() => startEditing(order)}>
                            <Pencil size={14} style={{ marginRight: '6px' }} />
                            Edit
                          </button>
                          <button
                            className="btn btn-outline"
                            style={{ padding: '8px 12px', color: '#DC2626', borderColor: '#FCA5A5' }}
                            disabled={deletingOrderId === order.id}
                            onClick={() => deleteOrder(order)}
                          >
                            <Trash2 size={14} style={{ marginRight: '6px' }} />
                            {deletingOrderId === order.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </>
                      )}
                      {order.status === 'captured' && !order.shipping_fee_paid && !order.shipping_fee_payment_method && !order.package_id && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={() => toggleOrderSelection(order.id)}
                          />
                          Select to combine
                        </label>
                      )}
                      {order.status === 'shipped' && (
                        <button
                          className="btn btn-primary"
                          style={{ padding: '8px 12px' }}
                          disabled={confirmingDeliveryId === order.id}
                          onClick={() => confirmDelivery(order)}
                        >
                          {confirmingDeliveryId === order.id ? 'Confirming...' : 'Confirm Delivery'}
                        </button>
                      )}
                      <div
                        className="status-pill"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: status.bg,
                          color: status.color,
                        }}
                      >
                        {status.icon}
                        {status.label}
                      </div>
                    </div>
                  </div>

                  {order.shipping_fee_amount != null && (
                    <div
                      style={{
                        marginBottom: '20px',
                        padding: '14px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        background: order.shipping_fee_paid ? '#F0FDF4' : '#FFFBEB',
                        border: `1px solid ${order.shipping_fee_paid ? '#BBF7D0' : '#FDE68A'}`,
                        color: order.shipping_fee_paid ? '#166534' : '#92400E',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '8px' }}>International Shipping Fee</div>
                      <div style={{ display: 'grid', gap: '4px' }}>
                        <div><strong>Amount:</strong> £{order.shipping_fee_amount.toFixed(2)}</div>
                        {order.shipping_fee_payment_method && (
                          <div><strong>Payment method:</strong> {paymentMethodLabels[order.shipping_fee_payment_method] || order.shipping_fee_payment_method}</div>
                        )}
                        {order.shipping_country && (
                          <div><strong>Destination country:</strong> {order.shipping_country}</div>
                        )}
                        {parseShippingAddress(order.shipping_address) && (
                          <div><strong>Destination address:</strong> {parseShippingAddress(order.shipping_address)}</div>
                        )}
                        <div>
                          <strong>Status:</strong>{' '}
                          {order.shipping_fee_paid
                            ? 'Paid'
                            : order.shipping_fee_payment_method === 'bank_transfer'
                              ? "Recorded via bank transfer — awaiting our team's confirmation"
                              : 'Awaiting payment'}
                        </div>
                      </div>
                    </div>
                  )}

                  {order.package_id && (
                    <div
                      style={{
                        marginBottom: '20px',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        background: packages[order.package_id]?.shipping_fee_paid ? '#F0FDF4' : '#EFF6FF',
                        border: `1px solid ${packages[order.package_id]?.shipping_fee_paid ? '#BBF7D0' : '#BFDBFE'}`,
                        color: packages[order.package_id]?.shipping_fee_paid ? '#166534' : '#1D4ED8',
                      }}
                    >
                      {packages[order.package_id] ? (
                        <>
                          Combined into a {packages[order.package_id].order_ids.length}-order package.{' '}
                          {packages[order.package_id].shipping_fee_amount == null
                            ? 'Shipping fee not yet paid.'
                            : packages[order.package_id].shipping_fee_paid
                              ? `Shipping fee paid: £${(packages[order.package_id].shipping_fee_amount ?? 0).toFixed(2)}.`
                              : `Shipping fee of £${(packages[order.package_id].shipping_fee_amount ?? 0).toFixed(2)} recorded via bank transfer — awaiting our team's confirmation.`}
                        </>
                      ) : (
                        'Combined into a package — set its shipping address and fee from any order in the package.'
                      )}
                      {!packages[order.package_id]?.shipping_fee_paid && !packages[order.package_id]?.shipping_fee_payment_method && (
                        <button
                          className="btn btn-outline"
                          style={{ padding: '4px 10px', fontSize: '12px', marginLeft: '10px' }}
                          onClick={() => setPackageModal({ id: order.package_id!, orderCount: orders.filter((o) => o.package_id === order.package_id).length })}
                        >
                          Pay Shipping Fee
                        </button>
                      )}
                    </div>
                  )}

                  {order.tracking_number && (
                    <div
                      style={{
                        background: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '6px',
                        padding: '16px',
                        marginBottom: '20px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', marginBottom: '2px' }}>Carrier</div>
                            <strong style={{ fontSize: '14px' }}>{order.carrier || 'N/A'}</strong>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#64748B', textTransform: 'uppercase', marginBottom: '2px' }}>Tracking #</div>
                            <strong style={{ fontSize: '14px' }}>{order.tracking_number}</strong>
                          </div>
                          {delivery && (
                            <div
                              className="status-pill"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: `${delivery.color}15`,
                                color: delivery.color,
                              }}
                            >
                              {delivery.icon}
                              {delivery.label}
                            </div>
                          )}
                        </div>
                        {order.tracking_url && (
                          <a
                            href={order.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline"
                            style={{
                              padding: '8px 16px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <ExternalLink size={14} />
                            Track
                          </a>
                        )}
                      </div>
                      {order.delivery_estimated && (
                        <div style={{ fontSize: '12px', color: '#64748B', marginTop: '10px' }}>
                          Estimated delivery:{' '}
                          <strong>{new Date(order.delivery_estimated).toLocaleDateString()}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  {order.status === 'delivered' && (
                    <div style={{
                      marginBottom: '20px', padding: '14px 16px', borderRadius: '6px', fontSize: '13px',
                      background: order.reward_credited ? '#F0FDF4' : order.invoice_status === 'rejected' ? '#FEF2F2' : '#FFFBEB',
                      border: `1px solid ${order.reward_credited ? '#BBF7D0' : order.invoice_status === 'rejected' ? '#FCA5A5' : '#FDE68A'}`,
                      color: order.reward_credited ? '#166534' : order.invoice_status === 'rejected' ? '#991B1B' : '#92400E',
                    }}>
                      {order.reward_credited
                        ? `Bonus Reward of £${(order.reward_credited_amount ?? 0).toFixed(2)} credited to your account.`
                        : order.invoice_status === 'rejected'
                          ? `No Bonus Reward for this order — ${order.invoice_rejected_reason || 'the supplier could not issue a compliant invoice.'}`
                          : 'Delivery confirmed — awaiting invoice verification before your Bonus Reward is released.'}
                      {order.reward_credited && order.actual_invoice_amount != null && (
                        <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                          Calculated from an invoice amount of £{order.actual_invoice_amount.toFixed(2)}.
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <p style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>
                        Items ({order.items.length})
                      </p>
                      <span style={{ fontWeight: 700, color: 'var(--accent-moss)' }}>
                        Total: £{order.total_amount.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {order.items.map((item, idx) => {
                        const imageReady = !!(item.image_filename && item.id && itemImageUrls[item.id]);
                        return (
                        <div
                          key={idx}
                          style={{
                            padding: '12px',
                            background: 'var(--bg-oatmeal)',
                            borderRadius: '4px',
                            fontSize: '13px',
                            display: 'grid',
                            gridTemplateColumns: imageReady ? '88px 1fr' : '1fr',
                            gap: '14px',
                          }}
                        >
                          {imageReady && (
                            <a href={itemImageUrls[item.id!]} target="_blank" rel="noopener noreferrer">
                              <img
                                src={itemImageUrls[item.id!]}
                                alt="Item"
                                style={{ width: '88px', height: '88px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-light)' }}
                              />
                            </a>
                          )}
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                              <div>
                                <strong>{item.retailer}</strong>
                                <span style={{ margin: '0 8px', color: '#ccc' }}>|</span>
                                <span style={{ color: '#666' }}>{item.category}</span>
                                {(item.quantity ?? 1) > 1 && (
                                  <span style={{ marginLeft: '8px', color: '#666' }}>× {item.quantity}</span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'var(--accent-moss)' }}
                                >
                                  <ExternalLink size={14} />
                                </a>
                                <span style={{ fontWeight: 600 }}>
                                  £{item.price.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            {(item.size || item.color || item.discount_code) && (
                              <div style={{ marginTop: '6px', color: '#64748B', fontSize: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                {item.size && <span>Size: {item.size}</span>}
                                {item.color && <span>Color: {item.color}</span>}
                                {item.discount_code && <span>Discount code: {item.discount_code}</span>}
                              </div>
                            )}
                            {item.id && ['pending_preauth', 'preauthorized', 'confirmed'].includes(order.status) && (
                              <div style={{ marginTop: '8px' }}>
                                <label style={{ fontSize: '12px', color: 'var(--accent-moss)', cursor: 'pointer' }}>
                                  {uploadingImageItemId === item.id
                                    ? 'Uploading...'
                                    : item.image_filename ? 'Replace image' : 'Attach image'}
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    style={{ display: 'none' }}
                                    disabled={uploadingImageItemId === item.id}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file && item.id) uploadItemImage(order.id, item.id, file);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          : !loading && <div className="section-card" style={{ padding: '32px', textAlign: 'center' }}>
            <p style={{ color: '#64748B', marginBottom: '20px', fontSize: '14px' }}>No smart orders yet.</p>
            <Link to="/order" className="btn btn-primary" style={{ textDecoration: 'none' }}>
              <Package size={16} /> Start Your First Order
            </Link>
          </div>}
      </div>

      {editingOrderId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000 }}>
          <div className="section-card" style={{ width: '100%', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto', padding: '24px' }}>
            <h2 className="card-title" style={{ marginBottom: '16px' }}>Edit pending order</h2>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Total amount</label>
                <input className="form-input" type="number" step="0.01" value={editTotalAmount} onChange={(e) => setEditTotalAmount(e.target.value)} />
              </div>
              {editItems.map((item, index) => (
                <div key={`${item.retailer}-${index}`} style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '16px', display: 'grid', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>Item {index + 1}</strong>
                    <button className="btn btn-outline" style={{ padding: '6px 10px' }} onClick={() => removeItem(index)}>Remove</button>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Retailer</label>
                    <input className="form-input" value={item.retailer} onChange={(e) => updateItem(index, 'retailer', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">URL</label>
                    <input className="form-input" value={item.url} onChange={(e) => updateItem(index, 'url', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price</label>
                    <input className="form-input" type="number" step="0.01" value={item.price} onChange={(e) => updateItem(index, 'price', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input className="form-input" value={item.category} onChange={(e) => updateItem(index, 'category', e.target.value)} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Quantity</label>
                      <input className="form-input" type="number" min={1} value={item.quantity || 1} onChange={(e) => updateItem(index, 'quantity', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Size</label>
                      <input className="form-input" value={item.size || ''} onChange={(e) => updateItem(index, 'size', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Color</label>
                      <input className="form-input" value={item.color || ''} onChange={(e) => updateItem(index, 'color', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Discount Code</label>
                    <input className="form-input" value={item.discount_code || ''} onChange={(e) => updateItem(index, 'discount_code', e.target.value)} />
                  </div>
                </div>
              ))}
              <button className="btn btn-outline" style={{ width: 'fit-content' }} onClick={addItem}>+ Add item</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button className="btn btn-outline" onClick={() => setEditingOrderId(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveOrder} disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {packageModal && (
        <PackageShippingModal
          packageId={packageModal.id}
          orderCount={packageModal.orderCount}
          onClose={() => {
            setPackageModal(null);
            fetchOrders();
          }}
          onDone={() => {
            setPackageModal(null);
            fetchOrders();
          }}
        />
      )}
    </div>
  );
};

export default Orders;
